import { uniq } from 'es-toolkit';

import {
  DirectedAcyclicGraph,
  getUserVisibleOrDefaultExceptionMessage,
  UniqueNameAllocator,
} from '@paragon/client-tools';
import { designService } from '@paragon/data-connections';
import {
  componentDesignToLayoutModel,
  getChildrenFinder,
  getComponentDesignDescendants,
  getInsertionsWithPendingChanges,
  getLayoutModelsWithPendingChanges,
  getProjectAncestor,
  layoutModelsCreated,
  saveChangesForSingleProject,
} from '@paragon/layout-models';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage, runWithSpinner } from '@paragon/task-spinner';

import { getScopeComponentDesignGuids, ProjectActionScope, requireSingleScopeProjectGuid } from '../projectActionScope';
import { RootState } from '../slices';
import { showComponentCopyConfirmationDialog, showComponentMovementConfirmationDialog } from './dialogActions';
import { AsyncThunkActionVoid } from './thunkActions';

export const moveComponentDesignsToContainer =
  (scope: ProjectActionScope, targetContainerGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const componentDesignGuids = new Set(getScopeComponentDesignGuids(scope));
    const insertions = getInsertionsWithPendingChanges(getState());
    const scopedInsertions = insertions.filter((insertion) => componentDesignGuids.has(insertion.objectGuid));
    const editableInsertions = scopedInsertions.filter((insertion) => insertion.type === 'NormalComponent');

    if (editableInsertions.length === 0) {
      dispatch(enqueueWarning('These component designs are part of a layout and cannot be moved.'));
      return;
    }

    const targetProject = getProjectAncestor(getState(), targetContainerGuid);

    const designsLeavingProjects = uniq(
      editableInsertions
        .filter((insertion) => {
          const sourceProject = getProjectAncestor(getState(), insertion.objectGuid);
          return sourceProject.guid !== targetProject.guid;
        })
        .map((insertion) => insertion.objectGuid),
    );

    const jackGirderConnections = await dispatch(
      runWithSpinner(
        'Preparing to move components...',
        designService.jackGirderConnections.getAllInvolving(designsLeavingProjects),
      ),
    );

    const designsInRelationships = designsLeavingProjects.filter((guid) =>
      jackGirderConnections.some((c) => guid === c.jackGuid || guid === c.girderGuid),
    );

    dispatch(
      showComponentMovementConfirmationDialog({
        targetContainerGuid,
        insertions: editableInsertions,
        someLayoutComponentsIgnored: scopedInsertions.length > editableInsertions.length,
        componentDesignGuidsInRelationships: designsInRelationships,
      }),
    );
  };

export const copyComponentDesignsToContainer =
  (scope: ProjectActionScope, targetContainerGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const sourceProjectGuid = requireSingleScopeProjectGuid(scope, dispatch);
    if (sourceProjectGuid == null) {
      return;
    }

    const componentDesignGuids = getScopeComponentDesignGuids(scope);
    const targetProject = getProjectAncestor(getState(), targetContainerGuid);
    if (sourceProjectGuid === targetProject.guid) {
      dispatch(enqueueError('Target project cannot be the same as the source project'));
      return;
    }

    const namesThatWouldBeDuplicated = getNamesThatWouldBeDuplicatedByCopyIntoProject(
      getState(),
      targetProject.guid,
      componentDesignGuids,
    );
    const carriedComponentConnections = await designService.jackGirderConnections.getAllForProject(sourceProjectGuid);
    const layoutModels = getLayoutModelsWithPendingChanges(getState());

    const componentDesignLayoutModelsForSourceProject = getComponentDesignDescendants(
      sourceProjectGuid,
      getChildrenFinder(getState()),
    );
    const directedAcyclicGraph = DirectedAcyclicGraph.fromComponentDesignsAndJackGirderConnections(
      componentDesignLayoutModelsForSourceProject,
      carriedComponentConnections,
    );

    const componentDesignGuidSet = new Set(componentDesignGuids);
    const componentDesignDescendantsNotIncluded: string[] = [];
    for (const componentDesignGuid of componentDesignGuids) {
      const descendantGuids = directedAcyclicGraph.getDescendantGuids(componentDesignGuid);
      const componentDesignDescendantsNotIncludedForThisComponent = uniq(
        descendantGuids
          .filter((descendantGuid) => !componentDesignGuidSet.has(descendantGuid))
          .map((descendantGuid) => layoutModels[descendantGuid].name),
      );
      componentDesignDescendantsNotIncluded.push(...componentDesignDescendantsNotIncludedForThisComponent);
    }

    if (namesThatWouldBeDuplicated.length > 0 || componentDesignDescendantsNotIncluded.length > 0) {
      dispatch(
        showComponentCopyConfirmationDialog({
          componentDesignGuids,
          targetContainerGuid,
          componentDesignDescendantsNotIncluded,
          namesThatWouldBeDuplicated,
        }),
      );
    } else {
      dispatch(copyComponentDesignsToContainerWithoutConfirmation(componentDesignGuids, targetContainerGuid));
    }
  };

function getNamesThatWouldBeDuplicatedByCopyIntoProject(
  state: RootState,
  targetProjectGuid: string,
  componentDesignGuids: readonly string[],
): string[] {
  const layoutModels = getLayoutModelsWithPendingChanges(state);
  const nameAllocator = new UniqueNameAllocator(
    getComponentDesignDescendants(targetProjectGuid, getChildrenFinder(state)).map((model) => model.name),
  );

  const namesThatWouldBeDuplicated: string[] = [];
  for (const componentDesignGuid of componentDesignGuids) {
    const name = layoutModels[componentDesignGuid].name.trim();
    if (nameAllocator.allocate(name) !== name) {
      namesThatWouldBeDuplicated.push(name);
    }
  }

  return namesThatWouldBeDuplicated;
}

/**
 * This is the follow up to `copyComponentDesignsToContainer`; only call it from there or the dialog it spawns.
 */
export const copyComponentDesignsToContainerWithoutConfirmation =
  (componentDesignGuids: string[], targetContainerGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Copying components...'));

    try {
      const targetProject = getProjectAncestor(getState(), targetContainerGuid);

      // save target project to ensure name de-deplication considers any added but not-yet-saved components
      try {
        await dispatch(saveChangesForSingleProject(targetProject.guid));
      } catch (e) {
        console.error(e);
        dispatch(enqueueError('Error saving destination project before copying'));

        return;
      }

      const targetContainerMaterialSelection =
        getLayoutModelsWithPendingChanges(getState())[targetContainerGuid].materialSelection;

      const duplicatedComponentDesignsWithTimestamps =
        await designService.componentDesigns.postDuplicateMultipleComponentDesignsWithoutSettings(
          targetProject.guid,
          componentDesignGuids,
          targetContainerMaterialSelection,
        );

      dispatch(
        layoutModelsCreated({
          projectGuid: targetProject.guid,
          models: duplicatedComponentDesignsWithTimestamps.map(({ componentDesign }) =>
            componentDesignToLayoutModel(componentDesign),
          ),

          // note: unlike moving, copying does include layout components, so we don't filter them out
          insertions: duplicatedComponentDesignsWithTimestamps.map((design) => ({
            type: 'NormalComponent',
            parentGuid: targetContainerGuid,
            objectGuid: design.componentDesign.guid,
          })),
        }),
      );
    } catch (e) {
      console.error(e);
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(e, 'Error copying components')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
